import eventBus from '../events/eventBus';
import { showAlert } from '../Util/alerts';
import { getCurrentUser, getQueryParams, isLocal, showModal } from '../Util/helper';
import * as messenger from '../Util/messenger';
import { showTermsChangesModal } from '../Util/terms';

let socket = null;
let socketConnected = false;
let reconnectInterval = 1000;
let reconnectTimeout = null;
let previousMessageCount = 0;
let heartbeatInterval = null;

export async function connectWebSocket() {
    if (socketConnected && socket && socket.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        return;
    }

    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }

    const wsUrl = await getWebSocketUrl();
    if (!wsUrl) {
        console.error('Failed to determine WebSocket URL');
        return;
    }

    console.log(`Attempting to connect to WebSocket: ${wsUrl}`);

    try {
        socket = new WebSocket(wsUrl);

        socket.onopen = handleSocketOpen;
        socket.onmessage = handleSocketMessage;
        socket.onclose = handleSocketClose;
        socket.onerror = handleSocketError;

    } catch (error) {
        console.error('Failed to create WebSocket connection:', error);
        socketConnected = false;
        attemptReconnect();
    }

    if (alertUser?.unreadNotifications) {
        previousMessageCount = alertUser.unreadNotifications;
    }
    messenger.displayUnreadMessages();
}

async function getWebSocketUrl() {
    const isLocalhost = window.location.href.includes('localhost');
    const isDönerli = window.location.hostname.includes('dönerli') || 
                      window.location.hostname.includes('xn--dnerli');
    const deviceId = localStorage.getItem('deviceId') || crypto.randomUUID();
    localStorage.setItem('deviceId', deviceId);

    if (isLocalhost) {
        return 'ws://localhost:3003/account?deviceId=' + deviceId;
    }
    
    if (isDönerli) {
        try {
            const response = await fetch('https://dönerli.net/api/v1/auth/ws-token', {
                credentials: 'include'
            });
            
            if (!response.ok) {
                console.error('Failed to get WebSocket token');
                return null;
            }
            
            const { token } = await response.json();
            return `wss://diefettebrxnette.xyz/account?token=${token}&deviceId=${deviceId}`;
        } catch (error) {
            console.error('Error fetching WebSocket token:', error);
            return null;
        }
    }
    
    return 'wss://diefettebrxnette.xyz/account?deviceId=' + deviceId;
}

function handleSocketOpen() {
    console.log('✅ WebSocket connection established');
    socketConnected = true;
    reconnectInterval = 1000;

    startHeartbeat();
}

function startHeartbeat() {
    if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
    }

    heartbeatInterval = setInterval(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
        } else {
            clearInterval(heartbeatInterval);
            heartbeatInterval = null;
        }
    }, 25000);
}


function stopHeartbeat() {
    if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
    }
}

function handleSocketMessage(event) {
    try {
        console.log('📨 WebSocket message received:', event.data);
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
    } catch (error) {
        console.error('Error parsing WebSocket message:', error);
    }
}

function handleSocketClose(event) {
    socketConnected = false;
    console.error('❌ WebSocket connection closed');

    const closeReasons = {
        1000: 'Normal closure',
        1006: 'Abnormal closure (no close frame received) - possible network issue or server crash',
        1008: 'Policy violation - likely authentication issue',
        1011: 'Server error occurred'
    };

    console.log(closeReasons[event.code] || 'Unknown close code: ' + event.code);

    attemptReconnect();
}

function handleSocketError(error) {
    console.error('❌ WebSocket error occurred:', error);
}

function attemptReconnect() {
    if (reconnectTimeout) {
        return;
    }

    reconnectTimeout = setTimeout(() => {
        reconnectTimeout = null;
        socketConnected = false;
        console.log(`Attempting to reconnect... (interval: ${reconnectInterval}ms)`);
        connectWebSocket();
    }, reconnectInterval);

    reconnectInterval = Math.min(reconnectInterval * 2, 30000);
}

export function disconnectWebSocket() {
    stopHeartbeat();

    if (socket) {
        socket.close(1000, 'User disconnected');
        socket = null;
        socketConnected = false;
    }
    
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }
}

export function sendWebSocketMessage(data) {
    if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(data));
        return true;
    } else {
        console.error('Cannot send message: WebSocket is not connected');
        return false;
    }
}

function handleWebSocketMessage(data) {
    const handlers = {
        'UPDATE': handleUpdate,
        'IMPORTANT_UPDATE': handleImportantUpdate,
        'NEW_MESSAGE': handleNewMessage,
        'FRIENDLIST_UPDATE': handleFriendlistUpdate,
        'GLOBAL_EVENT': handleGlobalEvent,
        'POINT_UPDATE': handlePointUpdate,
        'SESSION_REFRESH_REQUIRED': handleSessionRefresh,
        'SESSION_INVALIDATED': handleSessionInvalidation,
        'USER_UPDATE': handleUserUpdate,
        'connectionEstablished': handleConnectionEstablished,
        'heartbeat': handleHeartbeatResponse
    };

    const handler = handlers[data.type];
    if (handler) {
        handler(data.content);
    } else {
        console.log('Unknown WebSocket message type:', data.type, 'Content:', data.content);
    }
}

function handleUpdate(content) {
    if (content.messageCount > previousMessageCount) {
        previousMessageCount = content.messageCount;
        showAlert(t('msg.some', { count: content.messageCount }), 'info', 5000);
    }
}

function handleImportantUpdate(content) {
    if (content.needsToAcceptPrivacy || content.needsToAcceptTermsOfService) {
        showTermsChangesModal();
        const titleEl = document.getElementById('termsChangesTitle');
        const textEl = document.getElementById('termsChangesText');
        
        if (titleEl) titleEl.innerHTML = content.title;
        if (textEl) textEl.innerHTML = content.text;
    }
}

function handleNewMessage(content) {
    showAlert(t('msg.new'), 'info', 5000);
    messenger.displayUnreadMessages();
}

function handleFriendlistUpdate(content) {
    const updateHandlers = {
        'friendRequestSent': () => {
            if (typeof addFriendRequest === 'function') {
                addFriendRequest(content.user, 'friendRequestsSentGrid');
            }
        },
        'friendRequestAccepted': () => {
            if (typeof removeFriendFromFriendList === 'function') {
                removeFriendFromFriendList(content.user);
            }
            if (typeof displayFriendInFriendList === 'function') {
                displayFriendInFriendList(content.user);
            }
        },
        'friendRequestReceived': () => {
            if (typeof addFriendRequest === 'function') {
                addFriendRequest(content.user, 'friendRequestsGrid');
            }
        },
        'friendRequestDeclined': () => {
            if (typeof removeFriendFromFriendList === 'function') {
                removeFriendFromFriendList(content.user);
            }
        },
        'friendRemoved': () => {
            if (typeof removeFriendFromFriendList === 'function') {
                removeFriendFromFriendList(content.user);
            }
        }
    };

    const handler = updateHandlers[content.type];
    if (handler) {
        handler();
        if (typeof toggleNoFriendsDisplay === 'function') {
            toggleNoFriendsDisplay();
        }
    }

    if (isLocal()) {
        console.log('Friend update:', content);
    }
}

function handleGlobalEvent(content) {
    showAlert(content.message || 'Global event received!', 'info', 5000);
}

// reward points update
function handlePointUpdate(content) {
    const elementUpdates = {
        'navPoints': content.totalPoints || '0',
        'monthlyRank': content.rank,
        'allTimeRank': content.allTimeRank,
        'monthlyPoints': content.monthlyPoints,
        'totalPoints': content.totalPoints,
        "navMonthlyPoints": '<i class="fa-regular fa-calendar mr-2"></i>' + t('activity.points', {points: content.monthlyPoints}),
        "navTotalPoints": '<i class="fa-regular fa-star mr-2"></i>' + t('activity.points', {points: content.totalPoints}),
    };

    Object.entries(elementUpdates).forEach(([id, value]) => {
        const element = document.getElementById(id);
        if (element && value !== undefined) {
            element.innerHTML = value;
        }
    });

    const statusElement = document.getElementById('eligibilityStatus');
    if (statusElement && content.status) {
        statusElement.classList.remove('text-warning');
        statusElement.classList.add('text-primary');
        statusElement.innerHTML = t('dash.rewardable');
    }

    if (alertUser) {
        alertUser.totalPoints = content.totalPoints;
        alertUser.monthlyPoints = content.monthlyPoints;
        alertUser.rank = content.rank;
        alertUser.allTimeRank = content.allTimeRank;
    }

    const currentUser = getCurrentUser();
    const hideRewardModal = currentUser?.settings?.preferences?.hide?.includes('rewardModal');
    
    if (!hideRewardModal && content.newPoints > 0) {
        if (getQueryParams()['modal'] == null) {
            rewardModalQueue = null;
            showModal('rewardModal', content);
        } else {
            queueRewardModal(content);
        }
    }

    eventBus.emit('user:update', {
        ...alertUser,
        ...content
    });
    if (isLocal()) {
        console.log('Points updated:', content);
    }
}

let rewardModalQueue = null;
function queueRewardModal(content) {
    if (rewardModalQueue) {
        rewardModalQueue.newPoints += content.newPoints;
        rewardModalQueue.totalPoints = content.totalPoints;
        rewardModalQueue.monthlyPoints = content.monthlyPoints;
        return;
    }

    rewardModalQueue = content;

    eventBus.on('modal:close', () => {
        if (rewardModalQueue) {
            showModal('rewardModal', rewardModalQueue);
            rewardModalQueue = null;
        }
    });
}

// session update received, refresh session
async function handleSessionRefresh(content) {
    // Update local user data if provided
    if (content.user) {
        updateLocalUserData(content.user);
        
        // Trigger custom event for app to listen to
        eventBus.emit('user:updated', content.user);

        if (content.message) {
            showAlert(content.message, 'info', 5000);
        }
    }

    try {
        const response = await fetch('/api/v1/auth/update-session', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (response.ok) {
            const data = await response.json();
            if (data.success && data.user) {
                updateLocalUserData(data.user);
                if (isLocal()) {
                    console.log('✅ Session refreshed successfully');
                }
            }
        } else {
            console.error('Failed to refresh session:', response.status);
        }
    } catch (error) {
        console.error('Error refreshing session:', error);
    }
}

// force logout
function handleSessionInvalidation(content) {
    console.log('🚫 Session invalidated:', content.reason);

    const message = content.message || t('errors.sessionInvalidated') || 
                    'Your session has been terminated. Redirecting to login...';
    showAlert(message, 'warning', 5000);
    clearLocalUserData();
    disconnectWebSocket();

    setTimeout(() => {
        const reason = content.reason || 'session_expired';
        window.location.href = `/account/login?reason=${reason}`;
    }, 2000);
}

function handleUserUpdate(content) {
    eventBus.emit('user:update', content);

    if (isLocal()) {
        showAlert('Local user data updated', 'info', 3000);
    }
}

eventBus.on('user:update', (userData) => {
    handleSessionRefresh({user: userData});
});

function handleConnectionEstablished(content) {
    if (isLocal()) {
        console.log('✅ WebSocket connection established at:', content.timestamp);
    }
    
    if (content.error) {
        console.warn('Warning during connection:', content.error);
    }
}

function handleHeartbeatResponse(content) {
    // do nothing
}

function updateLocalUserData(userData) {
    try {
        if (alertUser) {
            alertUser = { ...alertUser, ...userData };
        }

        eventBus.emit('user:updated', userData);
    } catch (error) {
        console.error('Error updating local user data:', error);
    }
}


function clearLocalUserData() {
    try {
        localStorage.clear();
        sessionStorage.clear();
        
        if (alertUser) {
            alertUser = null;
        }
    } catch (error) {
        console.error('Error clearing local user data:', error);
    }
}

export function isConnected() {
    return socketConnected && socket && socket.readyState === WebSocket.OPEN;
}

// current socket status
export function getSocketStatus() {
    if (!socket) return 'disconnected';
    
    const states = {
        [WebSocket.CONNECTING]: 'connecting',
        [WebSocket.OPEN]: 'connected',
        [WebSocket.CLOSING]: 'closing',
        [WebSocket.CLOSED]: 'disconnected'
    };
    
    return states[socket.readyState] || 'unknown';
}